import { axiosBaseQuery } from '@api/api';
import { createApi } from '@reduxjs/toolkit/query/react';

export type BillingPlanKey = string;
export type BillingPlanStatus = string;
export type BillingLimitValue = number | 'unlimited';

export interface BillingPlanLimits {
  maxProjects?: BillingLimitValue;
  maxOrgMembers?: BillingLimitValue;
  maxProjectMembers?: BillingLimitValue;
  maxChannelsPerProject?: BillingLimitValue;
  monthlyBundleUploads?: BillingLimitValue;
  maxBundleSizeMB?: BillingLimitValue;
  maxStorageUsedGB?: BillingLimitValue;
}

export interface BillingPlanCapabilities {
  notifications?: boolean;
}

export interface BillingPlanPricing {
  currency: string;
  monthly?: number;
  annual?: number;
  billingUnit?: string;
}

export interface BillingPlan {
  key: BillingPlanKey;
  name: string;
  planRank?: number;
  isActive: boolean;
  highlights?: string[];
  capacity?: string[];
  limits: BillingPlanLimits;
  capabilities?: BillingPlanCapabilities;
  pricing?: BillingPlanPricing;
}

export interface BillingSubscription {
  plan: BillingPlanKey;
  status: BillingPlanStatus;
  provider?: 'stripe' | 'paddle' | null;
  isActive: boolean;
  effectivePlan: BillingPlanKey;
  billingInterval?: StripeBillingInterval;
  currentPeriodEnd?: string;
  startedAt?: string;
  endsAt?: string | null;
  scheduledChange?: BillingScheduledChange | null;
  refund?: BillingRefund | null;
}

export interface BillingRefund {
  provider: 'paddle';
  type: 'full' | 'partial';
  status: 'pending_approval' | 'approved' | 'rejected';
  requestedAt: string;
  resolvedAt?: string;
}

export interface BillingScheduledChange {
  type: 'downgrade' | 'cancel';
  fromPlan: BillingPlanKey;
  toPlan: BillingPlanKey;
  effectiveAt: string;
  provider: 'paddle';
  status: 'pending' | 'applying' | 'paddle_applied';
  applyAt?: string;
  paddleAppliedAt?: string;
  attempts?: number;
  lastAttemptAt?: string;
  lastError?: string;
}

export interface BillingAccessGrantSummary {
  id: string;
  planKey: BillingPlanKey;
  sourceType: 'campaign' | 'admin_override';
  sourceId: string;
  startsAt: string;
  endsAt: string | null;
  notes: string;
}

export interface BillingProjectMemberUsage {
  projectId?: string;
  projectName?: string;
  projectSlug?: string;
  current?: number;
  active?: number;
  pendingInvites?: number;
}

export interface BillingProjectChannelUsage {
  projectId?: string;
  projectName?: string;
  projectSlug?: string;
  current?: number;
}

export interface BillingUsage {
  maxProjects?: { current?: number };
  maxOrgMembers?: { current?: number; active?: number; pendingInvites?: number };
  maxProjectMembers?: { byProject?: BillingProjectMemberUsage[] };
  maxChannelsPerProject?: { byProject?: BillingProjectChannelUsage[] };
  monthlyBundleUploads?: { monthKey?: string; current?: number; totalBytesUploaded?: number };
  maxStorageUsed?: { currentBytes?: number };
}

export interface BillingPlansResponse {
  /** Present when the org has a Stripe customer; admins can open the billing portal. */
  hasStripeCustomer?: boolean;
  /** Present when the org has a Paddle customer; admins can open the billing portal. */
  hasPaddleCustomer?: boolean;
  availableCheckoutProviders?: Array<'stripe' | 'paddle'>;
  defaultCheckoutProvider?: 'stripe' | 'paddle' | null;
  effectivePlanSource?: 'free' | 'subscription' | 'campaign' | 'admin_override';
  activeAccessGrant?: BillingAccessGrantSummary | null;
  canRedeemCodes?: boolean;
  subscription?: BillingSubscription;
  plans?: BillingPlan[];
  usage?: BillingUsage;
}

export type StripeBillingInterval = 'month' | 'year';

export interface StripeCheckoutRequest {
  orgId: string;
  interval: StripeBillingInterval;
  planKey: BillingPlanKey;
}

export interface StripePortalRequest {
  orgId: string;
}

export interface StripeRedirectResponse {
  url: string;
}

export type CheckoutResponse = {
  provider: 'stripe' | 'paddle';
  url: string;
};

export interface SubscriptionChangeRequest {
  orgId: string;
  planKey: BillingPlanKey;
}

export type SubscriptionChangeType =
  | 'downgrade'
  | 'cancel'
  | 'scheduled_change_canceled';

export interface SubscriptionChangePreviewResponse {
  provider: 'paddle';
  fromPlan: BillingPlanKey;
  toPlan: BillingPlanKey;
  billingInterval?: StripeBillingInterval;
  prorationBillingMode?: 'prorated_immediately' | 'do_not_bill';
  immediateCharge?: {
    amount: string;
    currencyCode: string;
  } | null;
  scheduled?: true;
  changeType?: SubscriptionChangeType;
  effectiveAt?: string;
  currentPeriodEnd?: string;
  nextBilledAt?: string;
}

export interface SubscriptionChangeResponse {
  provider: 'paddle';
  updated?: true;
  scheduled?: true;
  changeType?: SubscriptionChangeType;
  fromPlan?: BillingPlanKey;
  toPlan?: BillingPlanKey;
  billingInterval?: StripeBillingInterval;
  effectiveAt?: string;
  subscription?: {
    plan: BillingPlanKey;
    status: BillingPlanStatus;
    billingInterval?: StripeBillingInterval;
    currentPeriodEnd?: string;
  };
}

export interface RedeemBillingCodeRequest {
  orgId: string;
  code: string;
}

export interface RedeemBillingCodeResponse {
  grant: BillingAccessGrantSummary & {
    orgId: string;
    sourceId: string;
    status: string;
    revokedAt?: string | null;
  };
  redemption: {
    id: string;
    campaignId: string;
    campaignName: string;
    campaignSlug: string;
    code: string;
    orgId: string;
    userId: string;
    grantId: string;
    redeemedAt: string;
  };
}

export const billingApi = createApi({
  reducerPath: 'billingApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Billing'],
  endpoints: builder => ({
    getOrganizationBillingPlans: builder.query<BillingPlansResponse, string>({
      query: orgId => ({ url: `/organizations/${orgId}/billing/plans` }),
      providesTags: (_result, _error, orgId) => [{ type: 'Billing', id: orgId }],
    }),
    createCheckoutSession: builder.mutation<CheckoutResponse, StripeCheckoutRequest>({
      query: ({ orgId, interval, planKey }) => ({
        url: `/organizations/${orgId}/billing/checkout`,
        method: 'POST',
        data: { interval, planKey },
      }),
    }),
    createBillingPortalSession: builder.mutation<StripeRedirectResponse, StripePortalRequest>({
      query: ({ orgId }) => ({
        url: `/organizations/${orgId}/billing/portal`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { orgId }) => [{ type: 'Billing', id: orgId }],
    }),
    previewSubscriptionChange: builder.mutation<
      SubscriptionChangePreviewResponse,
      SubscriptionChangeRequest
    >({
      query: ({ orgId, planKey }) => ({
        url: `/organizations/${orgId}/billing/subscription-change/preview`,
        method: 'POST',
        data: { planKey },
      }),
    }),
    updateSubscription: builder.mutation<SubscriptionChangeResponse, SubscriptionChangeRequest>({
      query: ({ orgId, planKey }) => ({
        url: `/organizations/${orgId}/billing/subscription-change`,
        method: 'POST',
        data: { planKey },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [{ type: 'Billing', id: orgId }],
    }),
    redeemBillingCode: builder.mutation<RedeemBillingCodeResponse, RedeemBillingCodeRequest>({
      query: ({ orgId, code }) => ({
        url: `/organizations/${orgId}/billing/redeem-code`,
        method: 'POST',
        data: { code },
      }),
      invalidatesTags: (_result, _error, { orgId }) => [{ type: 'Billing', id: orgId }],
    }),
  }),
});

export const {
  useGetOrganizationBillingPlansQuery,
  useCreateCheckoutSessionMutation,
  useCreateBillingPortalSessionMutation,
  usePreviewSubscriptionChangeMutation,
  useUpdateSubscriptionMutation,
  useRedeemBillingCodeMutation,
} = billingApi;
